import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  Box,
  Flex,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Tr,
  useBreakpointValue,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { hideAppSpinner } from "../../../../../context/Store/slices/appSpinnerSlice";
import { showErrorToast, translateSubElementString } from "../../../../../utils";
import * as React from "react";
import { Fragment } from "react";
import { LegendlessDonutChart, YearSelector } from "../../../../../components";
import { useGetCompanyTotalEmissionsReports } from "../../../../../hooks/query/reports";
import { QuarterSelector } from "../../../../../components/QuarterSelector";
import { NumberField } from "../../../../../components/NumberField";
import { scopeThreeCategories } from "../helpers";
import { CgNotes } from "react-icons/cg";
import { NotesModal } from "./components";
import { Card } from "../../../../../components/Card";

function TotalCompanyEmissionsTab({ selectedYear, setSelectedYear, selectedQuarter, setSelectedQuarter }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "companyEmissionsDashboard"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const dispatch = useDispatch();
  const toast = useToast();

  const { isOpen: isNotesModalOpen, onOpen: onNotesModalOpen, onClose: onNotesModalClose } = useDisclosure();

  const containerFlow = useBreakpointValue({
    xl: "row",
    base: "column"
  });

  const selectorFlow = useBreakpointValue({
    sm: "row",
    base: "column"
  });

  const {
    data: totalEmissionsData,
    isLoading,
    isFetching
  } = useGetCompanyTotalEmissionsReports({ year: selectedYear, quarter: selectedQuarter }, {
    onError: err => handleErrors(err),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0
  });

  const handleErrors = (error) => {
    if (error.response?.status === 404) {
      return;
    }
    let errorMessage = error.response?.data?.parameters?.message ?? error.message;
    showErrorToast(toast, errorMessage);
  };

  const getPieChartData = () => {
    return scopeThreeCategories
      .filter(el =>
        totalEmissionsData[`category${el.numberKey}TotalCo2Emissions`] &&
        totalEmissionsData[`category${el.numberKey}TotalCo2Emissions`] > 0)
      .map(el => {
        return {
          ...el,
          id: el.index,
          label: globalTranslation(`dataEntryPlanning.modal.categories.${el.translationKey}`),
          value: totalEmissionsData[`category${el.numberKey}TotalCo2Emissions`]
        };
      });
  };

  const getDonutChartTooltip = (tooltipObj) => {
    const { datum: { data } } = tooltipObj;
    const formattedValue = data.value && data.value > 0 ?
      data.value.toLocaleString("en-GB", { maximumFractionDigits: 4 }) : "";

    return (
      <Box position="relative">
        <Card
          position="absolute"
          minWidth={"250px"}
          padding={3}
          right={"100%"}
        >
          <Text fontSize="sm">
            Category {data.index}: {data.label} - <b>{formattedValue}</b> tCO<sub>2e</sub>
          </Text>
        </Card>
      </Box>);
  };

  return (
    <Box>
      <Flex gap={selectorFlow === "row" ? 4 : 0} flexFlow={selectorFlow}>
        <YearSelector selectedYear={selectedYear} handleYearSelection={(value) => setSelectedYear(value)} />
        <QuarterSelector
          selectedQuarter={selectedQuarter}
          handleQuarterSelection={(value) => setSelectedQuarter(value)}
        />
      </Flex>
      {!isFetching && !isLoading && !totalEmissionsData ?
        <Text fontSize="lg" fontWeight="bold">
          {globalTranslation("noDataAvailable")}
        </Text> :
        !isFetching && !isLoading && totalEmissionsData && Object.keys(totalEmissionsData).length > 0 ?
          <Flex height="100%" marginTop={8} flexFlow={containerFlow} gap={containerFlow === "row" ? 0 : 4}>
            <Box
              width={containerFlow === "row" ? "25%" : "100%"}
              flexGrow="1" paddingInlineEnd={5} paddingInlineStart={containerFlow === "column" ? 5 : 0}
              borderRight={containerFlow === "row" ? "1px solid #d4d9e0" : null}
            >
              <Text fontSize="xl" fontWeight="600">
                {translateSubElementString("companyEmissionsDashboard.totalEmissions.allScopesTotals")}
              </Text>
              <Flex gap={8} flexFlow="column" marginTop={5} overflow="auto">
                <Flex alignItems="center" gap={8}>
                  <Text fontSize="lg">
                    {t("totalEmissions.scopeOne")}
                  </Text>
                  <NumberField fontSize="3xl" fontWeight="bold" value={totalEmissionsData.scopeOneTotalCo2Emissions} />
                </Flex>
                <Flex alignItems="center" gap={8}>
                  <Text fontSize="lg">
                    {t("totalEmissions.scopeTwo")}
                  </Text>
                  <NumberField fontSize="3xl" fontWeight="bold" value={totalEmissionsData.scopeTwoTotalCo2Emissions} />
                </Flex>
                <Flex alignItems="center" gap={8}>
                  <Text fontSize="lg">
                    {t("totalEmissions.scopeThree")}
                  </Text>
                  <NumberField
                    fontSize="3xl" fontWeight="bold"
                    value={totalEmissionsData.scopeThreeTotalCo2Emissions}
                  />
                </Flex>
              </Flex>
            </Box>
            <Box
              width={containerFlow === "row" ? "40%" : "100%"}
              flexGrow="1" paddingInline={5}
              borderRight={containerFlow === "row" ? "1px solid #d4d9e0" : null}
            >
              <Flex justifyContent="space-between">
                <Text fontSize="xl" fontWeight="600">
                  {translateSubElementString("companyEmissionsDashboard.totalEmissions.scopeThreeCategories")}
                </Text>
                <IconButton
                  aria-label="notes-button"
                  type="button"
                  icon={<CgNotes size="20" />}
                  flexShrink="0"
                  size="sm"
                  backgroundColor="transparent"
                  tabIndex={-1}
                  onClick={() => onNotesModalOpen()}
                  title={t("totalEmissions.notesButtonTooltip")}
                />
              </Flex>
              <Table variant="striped" colorScheme="gray" marginTop={5} size="sm">
                <Tbody>
                  {scopeThreeCategories.map((category, index) => {
                    return <Fragment key={`${category.numberKey}-${category.translationKey}`}>
                      <Tr fontSize="xs">
                        <Td>
                          {index + 1}. {globalTranslation(`dataEntryPlanning.modal.categories.${category.translationKey}`)}{index === 0 ? " - Total" : null}
                        </Td>
                        <Td isNumeric>
                          {
                            totalEmissionsData[`category${category.numberKey}TotalCo2Emissions`]?.toLocaleString("en-GB", { maximumFractionDigits: 4 }) ?? ""
                          }
                        </Td>
                      </Tr>
                      {index === 0 ? <>
                        <Tr fontSize="xs">
                          <Td paddingInlineStart={6}>
                            1a. {t("totalEmissions.waterUsageAndTreatment")}
                          </Td>
                          <Td isNumeric>
                            {totalEmissionsData["waterUsageTreatmentTotalCo2Emissions"]?.toLocaleString("en-GB", { maximumFractionDigits: 4 }) ?? ""}
                          </Td>
                        </Tr>
                        <Tr fontSize="xs">
                          <Td paddingInlineStart={6}>
                            1b. {t("totalEmissions.estimatedPurchasedGoodsAndServices")}
                          </Td>
                          <Td isNumeric>
                            {totalEmissionsData["estimatedPurchasedGoodsAndServicesTotalCo2Emissions"]?.toLocaleString("en-GB", { maximumFractionDigits: 4 }) ?? ""}
                          </Td>
                        </Tr>
                        <Tr fontSize="xs">
                          <Td paddingInlineStart={6}>
                            1c. {t("totalEmissions.supplierProvidedEmissions")}
                          </Td>
                          <Td isNumeric>
                            {totalEmissionsData["purchasedGoodsAndServicesTotalCo2Emissions"]?.toLocaleString("en-GB", { maximumFractionDigits: 4 }) ?? ""}
                          </Td>
                        </Tr>
                      </> : null
                      }
                    </Fragment>;
                  })}
                </Tbody>
              </Table>
            </Box>
            <Box
              width={containerFlow === "row" ? "35%" : "100%"}
              flexGrow="1" paddingInlineStart={5}
              paddingInlineEnd={containerFlow === "column" ? 5 : 0}
            >
              <Text fontSize="xl" fontWeight="600">
                {t("totalEmissions.categoriesPieChartTitle")}
              </Text>
              <Box marginTop={10}>
                <Box height={"300px"}>
                  <LegendlessDonutChart
                    data={getPieChartData()}
                    getTooltip={getDonutChartTooltip}
                    getLabel={(el) => {
                      return el.data.value && el.data.value > 0 ?
                        el.data.value.toLocaleString("en-GB", { maximumFractionDigits: 4 }) : "";
                    }}
                    getLabelsTextColor={(el) => {
                      const darkBackgrounds = [1, 2, 3, 4, 5, 6, 7, 13];
                      return darkBackgrounds.includes(el.data.index) ? "white" : "black";
                    }}
                  />
                </Box>
                <Box marginTop={5}>
                  <Text fontSize="lg">Categories :</Text>
                  <Flex marginTop={2} gap={4} flexWrap="wrap">
                    {scopeThreeCategories.map((cat, index) => {
                      return <Flex key={`legend-${cat.numberKey}`} gap={1} alignItems="center">
                        <Box width={5} height={5} borderRadius="50%" backgroundColor={cat.color} />
                        <Text>{index + 1}</Text>
                      </Flex>;
                    })}
                  </Flex>
                </Box>
              </Box>
            </Box>
          </Flex> : null
      }
      {isNotesModalOpen ?
        <NotesModal
          isOpen={isNotesModalOpen}
          onClose={onNotesModalClose}
          year={selectedYear}
          quarter={selectedQuarter}
        /> : null
      }
    </Box>
  );
}

export default TotalCompanyEmissionsTab;